import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Rating, TextField, Typography, IconButton, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import {
  BuyAndBuildListDocument,
  useAddBuyAndBuildTargetMutation,
  useShortlistBuyAndBuildTargetMutation,
  useBuyAndBuildListLazyQuery,
} from '../../../gql/generated/graphql';
import { MUI_RATING_SCALE } from '../BuyBuildPage';

interface Props {
  companyId: string | null;
}

export const AddBuyAndBuildForm = ({ companyId }: Props) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [rating, setRating] = useState<number | null>(MUI_RATING_SCALE);
  const [shouldShortlist, setShouldShortlist] = useState(false);

  const [addBuyAndBuildTarget, { loading }] = useAddBuyAndBuildTargetMutation();
  const [addToShortlist] = useShortlistBuyAndBuildTargetMutation();
  const [getBuyAndBuild] = useBuyAndBuildListLazyQuery();

  const handleSubmit = async () => {
    if (!companyId || !url) return;

    const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
    const quality = rating ? rating / MUI_RATING_SCALE : 1;

    const { data: buyAndBuild } = await getBuyAndBuild({
      variables: { companyId: parseInt(companyId), first: 10 },
    });

    const result = await addBuyAndBuildTarget({
      variables: {
        softwareCompanyId: parseInt(companyId),
        targetCompanyUrl: formattedUrl,
        quality,
        setManualScore: true,
      },
      refetchQueries: [
        {
          query: BuyAndBuildListDocument,
          variables: {
            companyId: parseInt(companyId),
            first: 10,
          },
        },
      ],
      awaitRefetchQueries: true,
    });

    if (shouldShortlist && result.data?.addBuyAndBuildTarget?.ok) {
      const { data: updatedData } = await getBuyAndBuild({
        variables: { companyId: parseInt(companyId), first: 10 },
      });

      const newTarget = updatedData?.companyAssesment?.edges?.[0]?.node?.buyAndBuildTargets?.edges
        ?.map(node => node?.node)
        ?.find(node => node?.company?.uniqueDomain === formattedUrl);

      if (newTarget) {
        await addToShortlist({
          variables: {
            softwareCompanyId: buyAndBuild?.companyAssesment?.edges?.[0]?.node?.dbId,
            targetCompanyId: newTarget.company.dbId,
          },
          refetchQueries: [
            {
              query: BuyAndBuildListDocument,
              variables: {
                companyId: parseInt(companyId),
                first: 10,
              },
            },
            {
              query: BuyAndBuildListDocument,
              variables: {
                companyId: parseInt(companyId),
                first: 10,
                isShortlisted: true,
              },
            },
          ],
        });
      }
    }

    // Reset form
    setUrl('');
    setName('');
    setRating(MUI_RATING_SCALE);
    setShouldShortlist(false);
  };

  return (
    <Box display="flex" gap={2} alignItems="flex-start">
      <TextField
        size="small"
        label={t('buyAndBuild.addForm.urlLabel')}
        value={url}
        onChange={e => setUrl(e.target.value)}
        sx={{ flexGrow: 1 }}
      />
      <TextField
        size="small"
        label={t('buyAndBuild.addForm.nameLabel')}
        value={name}
        onChange={e => setName(e.target.value)}
        sx={{ flexGrow: 1 }}
      />
      <IconButton size="small" onClick={() => setShouldShortlist(!shouldShortlist)} sx={{ alignSelf: 'center' }}>
        <Tooltip
          title={
            shouldShortlist
              ? t('buyAndBuild.removeFromShortListColumnTooltip')
              : t('buyAndBuild.addToShortlistColumnTooltip')
          }
        >
          {shouldShortlist ? <StarIcon /> : <StarOutlineIcon />}
        </Tooltip>
      </IconButton>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="caption">{t('buyAndBuild.addForm.qualityLabel')}</Typography>
        <Rating value={rating} onChange={(_, value) => setRating(value)} max={MUI_RATING_SCALE} precision={0.1} />
      </Box>
      <Button variant="contained" onClick={handleSubmit} disabled={!url || loading || !companyId}>
        {t('buyAndBuild.addForm.addButton')}
      </Button>
    </Box>
  );
};
